import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Trophy, Medal, Crown, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Leaderboard() {
  const navigate = useNavigate();
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLeaderboard();
  }, []);

  const fetchLeaderboard = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/leaderboard`, {
        credentials: 'include'
      });
      const data = await response.json();
      setLeaderboard(data);
    } catch (error) {
      toast.error('Помилка завантаження таблиці лідерів');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="w-6 h-6 text-yellow-500" />;
    if (rank === 2) return <Medal className="w-6 h-6 text-gray-400" />;
    if (rank === 3) return <Medal className="w-6 h-6 text-orange-600" />;
    return null;
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="grid-background min-h-screen">
        <nav className="border-b border-border/50 backdrop-blur-sm bg-background/80">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Button
                variant="ghost"
                onClick={() => navigate('/dashboard')}
                data-testid="back-button"
                className="flex items-center space-x-2"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Назад</span>
              </Button>
              <h1 className="text-xl font-bold text-foreground">Таблиця Лідерів</h1>
              <div className="w-24"></div>
            </div>
          </div>
        </nav>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <div className="text-center mb-8">
              <Trophy className="w-16 h-16 text-primary mx-auto mb-4" />
              <h2 className="text-4xl font-black text-foreground mb-2">Кращі Архітектори</h2>
              <p className="text-lg text-muted-foreground">Змагайся з найкращими та стань легендою</p>
            </div>

            {leaderboard.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground">Таблиця лідерів порожня</p>
              </div>
            ) : (
              <div className="space-y-4">
                {leaderboard.map((entry, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.05 }}
                    className={`bg-card/50 backdrop-blur-sm border p-6 rounded-xl flex items-center space-x-4 ${
                      entry.rank <= 3
                        ? 'border-primary/50 shadow-[0_0_20px_rgba(59,130,246,0.2)]'
                        : 'border-white/5'
                    } hover:border-primary/30 transition-all`}
                  >
                    <div className="flex items-center justify-center w-12 h-12 rounded-full bg-muted font-bold text-foreground">
                      {getRankIcon(entry.rank) || entry.rank}
                    </div>
                    
                    <Avatar className="w-12 h-12">
                      <AvatarImage src={entry.picture} alt={entry.name} />
                      <AvatarFallback>{entry.name?.charAt(0)}</AvatarFallback>
                    </Avatar>

                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-foreground">{entry.name}</h3>
                      <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                        <span>Рівень {entry.level}</span>
                        <span>•</span>
                        <span>{entry.xp} XP</span>
                      </div>
                    </div>

                    {entry.rank <= 3 && (
                      <div className={`px-4 py-2 rounded-full ${
                        entry.rank === 1 ? 'bg-yellow-500/20 text-yellow-500' :
                        entry.rank === 2 ? 'bg-gray-400/20 text-gray-400' :
                        'bg-orange-600/20 text-orange-600'
                      }`}>
                        <span className="font-bold">#{entry.rank}</span>
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
}